import React from 'react';
import type { WizardState } from '../types';
import type { StepError } from '../validateStep';
import ValidatedEmailInput from '../../../../components/ValidatedEmailInput';
import { formatPhone } from '../../../../utils/phoneFormatting';

interface Props {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
  errors?: StepError[];
}

export default function BillingStep({ state, onChange, errors = [] }: Props) {
  const hasError = (field: string) => errors.some((e) => e.field === field);
  const errCls = (field: string) => hasError(field) ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-300';
  const b = state.billing;
  const t = state.trial || {};
  const isTrial = b.subscriptionType === 'trial';
  const contacts = state.contacts || [];

  const updateBilling = (patch: Record<string, any>) => onChange({ billing: { ...b, ...patch } });
  const updateContact = (patch: Record<string, any>) => onChange({ billing: { ...b, billingContact: { ...b.billingContact, ...patch } } });
  const updateFiscal = (patch: Record<string, any>) => onChange({ billing: { ...b, fiscalYear: { ...b.fiscalYear, ...patch } } });

  // Sanity bounds for the subscription Start Date — guards against typos like "5555-05-06"
  // that pass the date input but slip through schema-level required-only validation.
  const today = new Date();
  const startDateMinYear = today.getFullYear() - 5;
  const startDateMaxYear = today.getFullYear() + 10;
  const formatYmd = (d: Date) => d.toISOString().slice(0, 10);
  const startDateMin = formatYmd(new Date(startDateMinYear, 0, 1));
  const startDateMax = formatYmd(new Date(startDateMaxYear, 11, 31));
  const rawStartDate = b.startDate || '';
  const parsedStartDate = rawStartDate ? new Date(rawStartDate) : null;
  const startDateOutOfRange = (() => {
    if (!rawStartDate) return false; // empty is handled by required-validator
    if (!parsedStartDate || Number.isNaN(parsedStartDate.getTime())) return true;
    const y = parsedStartDate.getUTCFullYear();
    return y < startDateMinYear || y > startDateMaxYear;
  })();
  const startDateError = startDateOutOfRange
    ? `Start Date must fall between ${startDateMinYear} and ${startDateMaxYear}.`
    : null;

  // Maximum valid day per fiscal-year start month (Feb allows 29 to permit leap-year starts).
  const MAX_DAYS_BY_MONTH: Record<number, number> = {
    1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30, 7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31,
  };
  const startMonth = b.fiscalYear?.startMonth ?? 1;
  const maxStartDay = MAX_DAYS_BY_MONTH[startMonth] ?? 31;
  const startDay = b.fiscalYear?.startDay ?? 1;
  const startDayInvalid = !Number.isFinite(startDay) || startDay < 1 || startDay > maxStartDay;
  const startDayError = startDayInvalid
    ? `Start Day must be between 1 and ${maxStartDay} for ${new Date(2000, startMonth - 1).toLocaleString('en', { month: 'long' })}.`
    : null;

  // When the start month changes, clamp the start day to that month's maximum so we never
  // persist an out-of-range combination (e.g. Feb 30).
  const handleStartMonthChange = (nextMonth: number) => {
    const nextMax = MAX_DAYS_BY_MONTH[nextMonth] ?? 31;
    const currentDay = b.fiscalYear?.startDay ?? 1;
    const clampedDay = Math.min(Math.max(currentDay, 1), nextMax);
    updateFiscal({ startMonth: nextMonth, startDay: clampedDay });
  };

  const handleStartDayChange = (raw: string) => {
    if (raw === '') {
      updateFiscal({ startDay: 1 });
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.min(Math.max(parsed, 1), maxStartDay);
    updateFiscal({ startDay: clamped });
  };
  const updateTrial = (patch: Record<string, any>) => onChange({ trial: { ...t, ...patch } });
  const updateTrialContact = (patch: Record<string, any>) => onChange({ trial: { ...t, departmentContact: { ...t.departmentContact, ...patch } } });

  // Find which contact is currently selected by matching billing contact data
  const selectedBillingContactId = (() => {
    if (!b.billingContact?.name && !b.billingContact?.email) return 'manual';
    const match = contacts.find(c => {
      const primaryEmail = c.emails.find(e => e.isPrimary) || c.emails[0];
      return c.name === b.billingContact?.name && primaryEmail?.address === b.billingContact?.email;
    });
    return match?._id || 'manual';
  })();

  // Find which trial contact is currently selected
  const selectedTrialContactId = (() => {
    if (!t.departmentContact?.name && !t.departmentContact?.email) return 'manual';
    const match = contacts.find(c => {
      const primaryEmail = c.emails.find(e => e.isPrimary) || c.emails[0];
      return c.name === t.departmentContact?.name && primaryEmail?.address === t.departmentContact?.email;
    });
    return match?._id || 'manual';
  })();

  // Handle billing contact selection from existing contacts
  const handleBillingContactSelect = (contactId: string) => {
    if (contactId === 'manual') {
      updateContact({ name: '', email: '', phone: '' });
      return;
    }
    const contact = contacts.find(c => c._id === contactId);
    if (contact) {
      const primaryEmail = contact.emails.find(e => e.isPrimary) || contact.emails[0];
      const primaryPhone = contact.phones.find(p => p.isPrimary) || contact.phones[0];
      updateContact({
        name: contact.name,
        email: primaryEmail?.address || '',
        phone: primaryPhone?.number || '',
      });
    }
  };

  // Handle trial contact selection from existing contacts
  const handleTrialContactSelect = (contactId: string) => {
    if (contactId === 'manual') {
      updateTrialContact({ name: '', email: '', phone: '', title: '' });
      return;
    }
    const contact = contacts.find(c => c._id === contactId);
    if (contact) {
      const primaryEmail = contact.emails.find(e => e.isPrimary) || contact.emails[0];
      const primaryPhone = contact.phones.find(p => p.isPrimary) || contact.phones[0];
      updateTrialContact({
        name: contact.name,
        email: primaryEmail?.address || '',
        phone: primaryPhone?.number || '',
        title: contact.title || '',
      });
    }
  };

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Subscription</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Billing Entity</label>
            <select value={b.billingEntity || 'department'} onChange={(e) => updateBilling({ billingEntity: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="department">Department</option>
              <option value="organization">Organization</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Type</label>
            <select value={b.subscriptionType || 'active'} onChange={(e) => updateBilling({ subscriptionType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="active">Active (Paid)</option>
              <option value="trial">Trial</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={rawStartDate}
              min={startDateMin}
              max={startDateMax}
              onChange={(e) => updateBilling({ startDate: e.target.value })}
              aria-invalid={startDateOutOfRange || hasError('billing.startDate') || undefined}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                startDateOutOfRange || hasError('billing.startDate')
                  ? 'border-red-400 ring-1 ring-red-200'
                  : errCls('billing.startDate')
              }`}
            />
            {startDateError && (
              <p className="mt-1 text-xs text-red-600">{startDateError}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
            <select value={b.billingCycle || 'annual'} onChange={(e) => updateBilling({ billingCycle: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contracted Amount (USD)</label>
            <input type="number" min="0" step="0.01" value={b.contractedAmount ?? 0} onChange={(e) => updateBilling({ contractedAmount: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Billing Contact</h3>
        {contacts.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select from existing contacts</label>
            <select value={selectedBillingContactId} onChange={(e) => handleBillingContactSelect(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="manual">-- Enter manually --</option>
              {contacts.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name} {c.isPrimary && '(Primary)'}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input type="text" value={b.billingContact?.name || ''} onChange={(e) => updateContact({ name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <ValidatedEmailInput value={b.billingContact?.email || ''} onChange={(v) => updateContact({ email: v })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-9" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input type="tel" value={b.billingContact?.phone || ''} onChange={(e) => updateContact({ phone: formatPhone(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="(555) 123-4567" />
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Fiscal Year</h3>
        <div className="grid grid-cols-2 gap-4 max-w-xs">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Month</label>
            <select value={startMonth} onChange={(e) => handleStartMonthChange(parseInt(e.target.value, 10))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('en', { month: 'long' })}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Day</label>
            <input
              type="number"
              min={1}
              max={maxStartDay}
              value={startDay}
              onChange={(e) => handleStartDayChange(e.target.value)}
              aria-invalid={startDayInvalid || hasError('billing.fiscalYear.startDay') || undefined}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                startDayInvalid || hasError('billing.fiscalYear.startDay')
                  ? 'border-red-400 ring-1 ring-red-200'
                  : errCls('billing.fiscalYear.startDay')
              }`}
            />
            {startDayError && (
              <p className="mt-1 text-xs text-red-600">{startDayError}</p>
            )}
          </div>
        </div>
      </section>

      {isTrial && (
        <section className="bg-amber-50 border border-amber-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-amber-900 mb-3">Trial Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-1">Duration (days)</label>
              <input type="number" min="7" max="90" value={t.durationDays ?? 30} onChange={(e) => updateTrial({ durationDays: parseInt(e.target.value) || 30 })} className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-1">Support Level</label>
              <select value={t.supportLevel || 'basic'} onChange={(e) => updateTrial({ supportLevel: e.target.value })} className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white">
                <option value="basic">Basic</option>
                <option value="guided">Guided</option>
              </select>
            </div>
            {contacts.length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-amber-800 mb-1">Select from existing contacts</label>
                <select value={selectedTrialContactId} onChange={(e) => handleTrialContactSelect(e.target.value)} className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white">
                  <option value="manual">-- Enter manually --</option>
                  {contacts.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name} {c.isPrimary && '(Primary)'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-1">Dept Contact Name</label>
              <input type="text" value={t.departmentContact?.name || ''} onChange={(e) => updateTrialContact({ name: e.target.value })} className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-1">Dept Contact Email</label>
              <ValidatedEmailInput value={t.departmentContact?.email || ''} onChange={(v) => updateTrialContact({ email: v })} className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white pr-9" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-amber-800 mb-1">Sales Notes</label>
              <textarea rows={2} value={t.salesNotes || ''} onChange={(e) => updateTrial({ salesNotes: e.target.value })} className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white" />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
